import * as React from 'react'

import { Section } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import InformationTooltip from 'modules/core/components/Inputs/InformationTooltip'
import LabeledCheckbox from 'modules/core/components/Inputs/LabeledCheckbox'
import Select from 'modules/core/components/Inputs/Select'
import Text from 'modules/core/components/Inputs/Text'
import i18n from 'modules/core/lib/i18n'

import type AllowanceForm from 'modules/timeoff/lib/AllowanceForm'

import carryOverExpiredOptions from './carryOverExpiredOptions'

interface Props {
  allowanceForm: AllowanceForm
  isHourly: boolean
}

const CarryOverSettings = ({ allowanceForm, isHourly }: Props) => {
  const carryOverUnitsLabel = isHourly
    ? i18n.t('time_off.allowances.page.labels.carry_over_hours')
    : i18n.t('time_off.allowances.page.labels.carry_over_days')

  const carryOverUnitsInformation = isHourly
    ? i18n.t('time_off.allowances.page.information.carry_over_hours')
    : i18n.t('time_off.allowances.page.information.carry_over_days')

  const unlimitedCarryOverLabel = isHourly
    ? i18n.t('time_off.allowances.page.labels.unlimited_carry_over_hours')
    : i18n.t('time_off.allowances.page.labels.unlimited_carry_over_days')

  const expireInMonthsLabel = isHourly
    ? i18n.t('time_off.allowances.page.labels.expire_in_months_hours')
    : i18n.t('time_off.allowances.page.labels.expire_in_months_days')

  const expireInMonthsInformation = isHourly
    ? i18n.t('time_off.allowances.page.information.expire_in_months_hours')
    : i18n.t('time_off.allowances.page.information.expire_in_months_days')

  return (
    <Section.Form
      illustration="calendar-clock"
      title={i18n.t('time_off.allowances.page.carry_over.title')}
      description={i18n.t('time_off.allowances.page.carry_over.description')}
      submitLabel={i18n.t('common.submit')}
      extra={{ 'data-intercom-target': 'timeoff-allowances-carry-over-settings' }}
    >
      <Text
        label={carryOverUnitsLabel}
        secondaryLabel={
          <InformationTooltip information={carryOverUnitsInformation} icon="question" />
        }
        field={allowanceForm.get('carry_over_units_in_cents')}
        readonly={allowanceForm.get('unlimited_carry_over').value}
        value={allowanceForm.get('unlimited_carry_over').value ? i18n.t('common.unlimited') : ''}
        extra={{ 'data-intercom-target': 'timeoff-allowances-carry-over-units-input' }}
      />
      <LabeledCheckbox
        field={allowanceForm.get('unlimited_carry_over')}
        label={unlimitedCarryOverLabel}
      />
      {allowanceForm.get('unlimited_carry_over_expiration').value ? (
        <Text
          label={expireInMonthsLabel}
          secondaryLabel={
            <InformationTooltip information={expireInMonthsInformation} icon="question" />
          }
          field={allowanceForm.get('carry_over_units_in_cents')}
          readonly
          value={i18n.t('common.unlimited')}
          extra={{ 'data-intercom-target': 'timeoff-allowances-expire-in-months-input' }}
        />
      ) : (
        <Select
          field={allowanceForm.get('expire_in_months')}
          label={expireInMonthsLabel}
          secondaryLabel={
            <InformationTooltip information={expireInMonthsInformation} icon="question" />
          }
          options={carryOverExpiredOptions()}
          showOriginalLabelText
          extra={{ 'data-intercom-target': 'timeoff-allowances-expire-in-months-input' }}
        />
      )}
      <LabeledCheckbox
        field={allowanceForm.get('unlimited_carry_over_expiration')}
        label={i18n.t('time_off.allowances.page.labels.unlimited_carry_over_expiration')}
      />
    </Section.Form>
  )
}

export default observer(CarryOverSettings)
